import React from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import PrintIcon from "@mui/icons-material/Print";
import IosShareIcon from "@mui/icons-material/IosShare";

import { styled } from "@mui/material/styles";
import { stepConnectorClasses } from "@mui/material/StepConnector";
import ApprovalRoundedIcon from "@mui/icons-material/ApprovalRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { mobiliTheme } from "../../../../themes/mobiliTheme";
import DoneAllIcon from "@mui/icons-material/DoneAll";
// const steps = ["Speichern", "Ausdrucke erstellen", "Versenden"];

const defaultSteps = [
  { label: "Rezepte Zusammensuchen", icon: SaveIcon },
  { label: "Taxieren", icon: ApprovalRoundedIcon },
  { label: "Urbelegbegleitschreiben", icon: PrintIcon },
  { label: "Versenden", icon: IosShareIcon },
];

const paperBillingSteps = [
  { label: "Rezept Zusammenstellen", icon: SaveIcon },
  { label: "Urbelegbegleitschreiben", icon: PrintIcon },
  { label: "Abschließen", icon: DoneAllIcon },
];

export interface BillingStepperProps {
  activeStep: number;
  isPaperBilling?: boolean; // Optional prop to indicate if it's paper billing
  hideProgress?: boolean; // Hide the circular progress indicator
}

export const BillingStepper: React.FC<BillingStepperProps> = ({
  activeStep,
  isPaperBilling = false,
  hideProgress = false,
}) => {
  const labels = isPaperBilling
    ? paperBillingSteps.map((s) => s.label)
    : defaultSteps.map((s) => s.label);

  const lenght = isPaperBilling ? paperBillingSteps.length : defaultSteps.length;
  const pct = (activeStep / lenght) * 100;

  const completedColor = "#86d989"; // or use mobiliTheme.palette.success.main;
  //mobiliTheme.palette.success.main;
  const activeColor = "#003059"; //mobiliTheme.mainHeader;
  const disabledIconColor = "#e5e5e5";

  const ColorfulConnector = styled(StepConnector)(() => ({
    // default line color
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: disabledIconColor,
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
      borderColor: mobiliTheme.mainHeader,
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
      borderColor: mobiliTheme.palette.success.main,
    },
  }));

  return (
    <Box sx={{ width: "100%", mb: 3, mt: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid
          item
          xs={12}
          sm={hideProgress ? 12 : 10}
          md={hideProgress ? 12 : 10}
          lg={hideProgress ? 12 : 10.5}
        >
          <Stepper alternativeLabel activeStep={activeStep} connector={<ColorfulConnector />}>
            {labels.map((label, index) => {
              const isCompleted = index < activeStep;
              const isActive = index === activeStep;

              const IconComponent = isPaperBilling
                ? paperBillingSteps[index].icon
                : defaultSteps[index].icon;
              const iconColor = isActive
                ? activeColor
                : isCompleted
                  ? completedColor
                  : disabledIconColor;

              const renderedIcon = isCompleted ? (
                <CheckCircleIcon sx={{ color: completedColor }} />
              ) : (
                <IconComponent sx={{ color: iconColor }} />
              );

              return (
                <Step key={label} completed={isCompleted} active={isActive}>
                  <StepLabel
                    icon={renderedIcon}
                    sx={{
                      "& .MuiStepLabel-label.Mui-completed": {
                        color: completedColor,
                      },
                      "& .MuiStepLabel-label.Mui-active": {
                        color: activeColor,
                      },
                      "& .MuiStepLabel-label": {
                        color: disabledIconColor,
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Grid>
        {!hideProgress && (
          <Grid
            item
            xs={12}
            sm={2}
            md={2}
            lg={1.5}
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 1,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                <Typography
                  variant="subtitle2"
                  component="div"
                  color={completedColor}
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                    fontWeight: "bold",
                    lineHeight: 1,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  {`${Math.round(pct)}%`}
                </Typography>
              </Box>
              <CircularProgress
                variant="determinate"
                value={pct}
                size={60}
                thickness={5}
                sx={{
                  color: completedColor,
                  "@media (max-width:600px)": {
                    width: "50px !important",
                    height: "50px !important",
                  },
                  "@media (min-width:601px) and (max-width:960px)": {
                    width: "55px !important",
                    height: "55px !important",
                  },
                }}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
